/**
 * Unified OAuth Service for all broker integrations
 * Handles OAuth flow consistently across Tradier, Tastytrade, Schwab, and IBKR
 */

import axiosInstance from '../../api/axios';
import { auth } from '../firebase';
import { processBackendResponse, prepareForBackend } from '../../utils/fieldMapper';

class UnifiedOAuthService {
  constructor() {
    this.supportedBrokers = ['tradier', 'tastytrade', 'schwab', 'ibkr'];
  }

  /**
   * Get the appropriate axios instance for a service
   * All services now use the same Firebase Functions endpoint
   * @param {string} serviceName - The service name (e.g., 'tradier_exchange_token')
   * @returns {object} Axios instance configured for the service
   */
  getAxiosInstance(serviceName) {
    // All services now use the unified axiosInstance
    return axiosInstance;
  }

  /**
   * Get the appropriate endpoint path for a service
   * @param {string} serviceName - The service name (e.g., 'tradier_exchange_token')  
   * @returns {string} The endpoint path
   */
  getEndpointPath(serviceName) {
    // All Firebase Functions use the service name as path
    return `/${serviceName}`;
  }

  /**
   * Get authorization URL from server for any broker
   * @param {string} broker - Broker name (tradier, tastytrade, schwab, ibkr)
   * @param {boolean} isSandbox - Whether to use sandbox/test environment
   * @returns {Promise<{url: string, state: string}>}
   */
  async getAuthorizationUrl(broker, isSandbox = false) {
    if (!this.supportedBrokers.includes(broker)) {
      throw new Error(`Unsupported broker: ${broker}`);
    }

    // Ensure user is authenticated
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to connect a broker');
    }

    // Generate redirect URI - always use /broker/callback for consistency
    const redirectUri = `${window.location.origin}/broker/callback`;
    
    // Check if production environment
    const isProduction = window.location.hostname.includes('web.app') || 
                        window.location.hostname.includes('firebaseapp.com');
    
    console.log(`🔗 OAuth initiation for ${broker}:`, {
      isSandbox,
      redirectUri,
      userAgent: navigator.userAgent,
      origin: window.location.origin,
      isProduction,
      hostname: window.location.hostname
    });

    try {
      const serviceName = `${broker}_create_state`;
      const serviceAxios = this.getAxiosInstance(serviceName);
      const endpointPath = this.getEndpointPath(serviceName);
      
      const response = await serviceAxios.post(endpointPath, {
        broker,
        isSandbox,
        redirectUri // Pass redirect URI to backend for validation
      });

      console.log(`📡 OAuth state creation API response status: ${response.status}`);
      console.log('Raw backend response:', response.data);

      if (response.data.success) {
        // Normalize backend response to frontend field names
        const normalized = processBackendResponse(response.data);
        console.log('Normalized response:', normalized);
        
        // Handle both oauth_url and authUrl field names
        const authUrl = normalized.authUrl || normalized.oauthUrl || response.data.oauth_url;
        
        console.log(`✅ OAuth state created for ${broker}:`, {
          state: normalized.state || response.data.state,
          authUrl: authUrl?.substring(0, 50) + '...',
          redirectUri: normalized.redirectUri || response.data.redirect_uri
        });
        
        const result = {
          url: authUrl,
          state: normalized.state || response.data.state,
          expiresIn: normalized.expiresIn,
          redirectUri: normalized.redirectUri || response.data.redirect_uri || redirectUri
        };
        
        console.log('Returning to component:', result);
        return result;
      }

      console.error(`⚠️ OAuth state creation returned success=false for ${broker}:`, response.data);
      throw new Error(response.data.error || 'Failed to create OAuth state');
    } catch (error) {
      console.error(`❌ OAuth state creation failed for ${broker}:`, error.message);
      
      // Enhanced error logging for debugging
      if (error.response) {
        console.error('Response error details:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
          url: error.config?.url
        });
      } else if (error.request) {
        console.error('No response received:', {
          url: error.config?.url,
          timeout: error.config?.timeout
        });
      }
      
      throw error;
    }
  }

  /**
   * Exchange authorization code for tokens
   * @param {string} broker - Broker name
   * @param {string} code - Authorization code from OAuth callback
   * @param {string} state - State parameter from OAuth callback
   * @param {boolean} isSandbox - Whether this is for sandbox environment
   * @param {string} redirectUri - The redirect URI used in the original request
   * @returns {Promise<Object>} Token exchange response
   */
  async exchangeCodeForToken(broker, code, state, isSandbox = false, redirectUri = null) {
    if (!this.supportedBrokers.includes(broker)) {
      throw new Error(`Unsupported broker: ${broker}`);
    }

    // Ensure user is authenticated
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to exchange tokens');
    }

    // Use provided redirect URI or generate default
    const finalRedirectUri = redirectUri || `${window.location.origin}/broker/callback`;
    
    // Check if production environment
    const isProduction = window.location.hostname.includes('web.app') || 
                        window.location.hostname.includes('firebaseapp.com');
    
    try {
      const serviceName = `${broker}_exchange_token`;
      const serviceAxios = this.getAxiosInstance(serviceName);
      const endpointPath = this.getEndpointPath(serviceName);
      
      const response = await serviceAxios.post(endpointPath, {
        code,
        state,
        isSandbox,
        redirectUri: finalRedirectUri // Include redirect URI for validation
      });

      if (response.data.success) {
        const result = {
          success: response.data.success,
          data: response.data.tokens || response.data.data || {},
          profile: response.data.account_info || response.data.profile || null,
          accounts: response.data.accounts || [],
          balances: response.data.balances || [],
          account_info: response.data.account_info || null,
          metadata: response.data.metadata || null,
          message: response.data.message
        };

        if (result.data) {
          result.data = processBackendResponse(result.data);
        }

        return result;
      }

      console.error(`⚠️ Token exchange returned success=false for ${broker}:`, response.data);
      throw new Error(response.data.error || 'Token exchange failed');
    } catch (error) {
      throw error;
    }
  }

  /**
   * Refresh access token for a broker
   * @param {string} broker - Broker name
   * @param {boolean} isSandbox - Whether this is for sandbox environment
   * @returns {Promise<Object>} Refresh token response
   */
  async refreshToken(broker, isSandbox = false) {
    if (!this.supportedBrokers.includes(broker)) {
      throw new Error(`Unsupported broker: ${broker}`);
    }

    // Ensure user is authenticated
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to refresh tokens');
    }

    console.log(`🔄 Refreshing token for ${broker}:`, { isSandbox, userId: user.uid });

    try {
      const response = await axiosInstance.post(`/${broker}_refresh_token`, {
        isSandbox
      });

      if (response.data.success) {
        // Normalize backend response
        const normalized = processBackendResponse(response.data);
        console.log(`✅ Token refresh successful for ${broker}`);
        return normalized;
      }
      
      // Handle specific error cases
      const errorData = response.data;
      if (errorData.error === 'token_expired_needs_reauth') {
        console.warn(`⚠️ ${broker} token expired and requires re-authentication:`, errorData.message);
        throw new Error(errorData.message);
      } else if (errorData.error === 'token_still_valid') {
        console.log(`ℹ️ ${broker} token is still valid:`, errorData.message);
        // Return success:false but with informative message
        return errorData;
      }

      throw new Error(errorData.error || errorData.message || 'Token refresh failed');
    } catch (error) {
      console.error(`❌ Token refresh failed for ${broker}:`, error);
      
      // Enhanced error logging
      if (error.response) {
        console.error('Refresh error details:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      
      throw error;
    }
  }

  /**
   * Get stored access token for a broker
   * @param {string} broker - Broker name
   * @param {boolean} isSandbox - Whether this is for sandbox environment
   * @returns {Promise<{accessToken: string, expiresAt: string}>}
   */
  async getAccessToken(broker, isSandbox = false) {
    if (!this.supportedBrokers.includes(broker)) {
      throw new Error(`Unsupported broker: ${broker}`);
    }

    // Ensure user is authenticated
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to retrieve tokens');
    }

    try {
      const response = await axiosInstance.post(`/${broker}_get_tokens`, {
        isSandbox
      });

      if (response.data.success) {
        // Normalize backend response
        const normalized = processBackendResponse(response.data);
        return {
          accessToken: normalized.accessToken,
          expiresAt: normalized.expiresAt
        };
      }

      // Handle specific error cases
      if (response.data.error === 'token_expired') {
        // Attempt to refresh the token
        const refreshResponse = await this.refreshToken(broker, isSandbox);
        if (refreshResponse.success) {
          // Retry getting the token after refresh
          return this.getAccessToken(broker, isSandbox);
        }
      }

      throw new Error(response.data.error || 'Failed to retrieve tokens');
    } catch (error) {
      console.error(`Failed to get access token for ${broker}:`, error);
      throw error;
    }
  }

  /**
   * Handle OAuth callback for any broker
   * @param {string} broker - Broker name
   * @param {URLSearchParams} params - URL search params from callback
   * @param {string} storedRedirectUri - The redirect URI stored during OAuth initiation
   * @returns {Promise<Object>} Processed callback response
   */
  async handleOAuthCallback(broker, params, storedRedirectUri = null) {
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    console.log(`🔍 OAuth callback for ${broker}:`, {
      hasCode: !!code,
      hasState: !!state,
      hasError: !!error,
      storedRedirectUri,
      currentUrl: window.location.href,
      broker: broker
    });

    // Check for OAuth errors
    if (error) {
      const errorMsg = `OAuth error: ${error} - ${errorDescription || 'No description'}`;
      console.error(`❌ ${errorMsg}`);
      throw new Error(errorMsg);
    }
    
    // If broker is unknown, we'll try all known brokers until one succeeds
    if (broker === 'unknown') {
      console.warn('⚠️ Broker type unknown, will attempt to determine from backend');
      // Try tradier first as it's most common
      const brokersToTry = ['tradier', 'tastytrade', 'schwab', 'ibkr'];
      
      for (const tryBroker of brokersToTry) {
        try {
          console.log(`🔄 Trying ${tryBroker} for token exchange...`);
          // Try to get sandbox mode from store or default to false
          const isSandbox = false; // Default to live for unknown broker
          const result = await this.exchangeCodeForToken(tryBroker, code, state, isSandbox, storedRedirectUri);
          if (result && result.success) {
            console.log(`✅ Successfully identified broker as ${tryBroker}`);
            // Update the result to include the correct broker type
            result.broker = tryBroker;
            return result;
          }
        } catch (e) {
          console.log(`❌ ${tryBroker} failed:`, e.message);
          // Continue to next broker
        }
      }
      
      throw new Error('Unable to determine broker type from OAuth callback');
    }

    // Validate required parameters
    if (!code || !state) {
      const errorMsg = 'Missing code or state parameter in OAuth callback';
      console.error(`❌ ${errorMsg}`, { code: !!code, state: !!state });
      throw new Error(errorMsg);
    }

    // Use stored redirect URI or generate default
    const redirectUri = storedRedirectUri || `${window.location.origin}/broker/callback`;

    // Exchange code for token - backend will validate state and redirect URI
    return this.exchangeCodeForToken(broker, code, state, false, redirectUri);
  }

  /**
   * Check if a broker integration exists and is active
   * @param {string} broker - Broker name
   * @param {boolean} isSandbox - Whether to check sandbox integration
   * @returns {Promise<boolean>}
   */
  async hasActiveIntegration(broker, isSandbox = false) {
    try {
      const tokenData = await this.getAccessToken(broker, isSandbox);
      const isActive = !!tokenData.accessToken;
      console.log(`🔍 Active integration check for ${broker}:`, { isActive, isSandbox });
      return isActive;
    } catch (error) {
      console.log(`❌ Active integration check failed for ${broker}:`, error.message);
      return false;
    }
  }

  /**
   * Disconnect/revoke broker integration
   * @param {string} broker - Broker name
   * @param {boolean} isSandbox - Whether this is for sandbox environment
   * @returns {Promise<void>}
   */
  async disconnectBroker(broker, isSandbox = false) {
    console.log('🔍 unifiedOAuthService.disconnectBroker called with broker:', broker, 'isSandbox:', isSandbox);
    if (!this.supportedBrokers.includes(broker)) {
      throw new Error(`Unsupported broker: ${broker}`);
    }

    // Ensure user is authenticated
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to disconnect a broker');
    }

    console.log(`🔌 Disconnecting ${broker} integration:`, { isSandbox, userId: user.uid });

    try {
      const response = await axiosInstance.post(`/${broker}_disconnect`, {
        isSandbox
      });

      if (response.data.success) {
        console.log(`✅ Successfully disconnected ${broker}`);
        
        // Delete all engagement data for this user when broker is disconnected - COMMENTED OUT
        // console.log('🗑️ Cleaning up engagement data for user:', user.uid);
        // try {
        //   const { manualDeleteEngagements } = await import('../../utils/manualDeleteEngagements');
        //   const result = await manualDeleteEngagements(user.uid);
        //   if (result.success) {
        //     console.log(`✅ Manual delete completed: ${result.deleted} records deleted`);
        //   } else {
        //     console.error('❌ Manual delete failed:', result.error);
        //   }
        // } catch (engagementError) {
        //   console.error('❌ Error cleaning up engagement data:', engagementError);
        //   // Don't throw error here, continue with disconnect
        // }
        
        return response.data;
      }

      throw new Error(response.data.error || 'Disconnect failed');
    } catch (error) {
      console.error(`❌ Failed to disconnect ${broker}:`, error);
      throw error;
    }
  }
}

// Export singleton instance
const unifiedOAuthService = new UnifiedOAuthService();
// Add error handling utility
unifiedOAuthService.createCrossCompatibleError = (message, details = {}) => {
  const error = new Error(message);
  error.details = details;
  error.timestamp = new Date().toISOString();
  error.userAgent = navigator.userAgent;
  error.url = window.location.href;
  return error;
};

// Add storage compatibility check
unifiedOAuthService.checkStorageCompatibility = () => {
  const results = {
    sessionStorage: false,
    localStorage: false,
    cookies: false
  };

  // Test sessionStorage
  try {
    window.sessionStorage.setItem('__test__', 'test');
    window.sessionStorage.removeItem('__test__');
    results.sessionStorage = true;
  } catch (e) {
    console.warn('SessionStorage not available:', e.message);
  }

  // Test localStorage
  try {
    window.localStorage.setItem('__test__', 'test');
    window.localStorage.removeItem('__test__');
    results.localStorage = true;
  } catch (e) {
    console.warn('LocalStorage not available:', e.message);
  }

  // Test cookies
  try {
    document.cookie = '__test__=test; path=/';
    results.cookies = document.cookie.includes('__test__=test');
    document.cookie = '__test__=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  } catch (e) {
    console.warn('Cookies not available:', e.message);
  }

  console.log('📊 Storage compatibility check:', results);
  return results;
};

export default unifiedOAuthService;